import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';

export interface ImageUploaderEntity {
  name: string;
  uuid: string;
}

export interface ImageUploaderItem {
  id: string;
  file: File;
  name: string;
  url: string;
  description: string;
  size: number;
  lastModified: number;
}

export interface ImageUploaderPayload {
  files: File[];
  entity: string;
  objectUuid: string;
  descriptions: string[];
}

@Component({
  selector: 'app-image-uploader',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule, TablerIconsModule],
  templateUrl: './image-uploader.component.html',
  styleUrl: './image-uploader.component.scss',
})
export class ImageUploaderComponent implements OnChanges, OnDestroy {
  @Input() entity: ImageUploaderEntity = { name: '', uuid: '' };
  @Input() initialPayload: ImageUploaderPayload | null = null;
  @Input() disabled = false;
  @Input() maxFiles = 8;
  @Input() maxFileSizeMb = 10;
  @Input() showHeader = false;
  @Input() title = 'Imagens da solicitacao';
  @Input() subtitle =
    'Adicione evidencias visuais e descreva o que cada imagem mostra.';
  @Input() emptyTitle = 'Clique ou arraste imagens';
  @Input() emptyDescription =
    'Use fotos do ambiente, do ativo ou do problema para enriquecer a solicitacao.';
  @Input() descriptionPlaceholder = 'Descricao da imagem';

  @Output() imagesChange = new EventEmitter<ImageUploaderItem[]>();

  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  images: ImageUploaderItem[] = [];
  activeImageId: string | null = null;
  isDragging = false;
  feedbackMessage = '';
  private initialPayloadSignature = '';

  get selectedImage(): ImageUploaderItem | null {
    return (
      this.images.find((image) => image.id === this.activeImageId) ??
      this.images[0] ??
      null
    );
  }

  get activeIndex(): number {
    const selectedImage = this.selectedImage;
    if (!selectedImage) return -1;

    return this.images.findIndex((image) => image.id === selectedImage.id);
  }

  get imageCountLabel(): string {
    if (this.images.length === 0) {
      return 'Nenhuma imagem adicionada.';
    }

    const hasMultipleImages = this.images.length > 1;

    return `${this.images.length} ${
      hasMultipleImages ? 'imagens' : 'imagem'
    } anexada${hasMultipleImages ? 's' : ''}.`;
  }

  get hasPreviousImage(): boolean {
    return this.activeIndex > 0;
  }

  get hasNextImage(): boolean {
    return this.activeIndex >= 0 && this.activeIndex < this.images.length - 1;
  }

  openFilePicker(): void {
    if (this.disabled) return;

    this.fileInput?.nativeElement.click();
  }

  handleStageKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    this.openFilePicker();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialPayload']) {
      this.applyInitialPayload();
    }
  }

  handleFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.addFiles(input.files);
    input.value = '';
  }

  handleDragEnter(event: DragEvent): void {
    if (this.disabled) return;

    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  handleDragOver(event: DragEvent): void {
    if (this.disabled) return;

    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  handleDragLeave(event: DragEvent): void {
    if (this.disabled) return;

    event.preventDefault();
    event.stopPropagation();

    if (event.currentTarget === event.target) {
      this.isDragging = false;
    }
  }

  handleDrop(event: DragEvent): void {
    if (this.disabled) return;

    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    this.addFiles(event.dataTransfer?.files ?? null);
  }

  addFiles(files: FileList | File[] | null): void {
    if (!files) return;

    this.feedbackMessage = '';

    const nextFiles = Array.from(files).filter((file) =>
      file.type.startsWith('image/'),
    );

    if (!nextFiles.length) {
      this.feedbackMessage = 'Selecione pelo menos uma imagem valida.';
      return;
    }

    const remainingSlots = this.maxFiles - this.images.length;
    if (remainingSlots <= 0) {
      this.feedbackMessage = `Limite de ${this.maxFiles} imagens atingido.`;
      return;
    }

    const acceptedFiles = nextFiles
      .filter((file) => this.isFileSizeAllowed(file))
      .filter((file) => !this.isDuplicatedFile(file))
      .slice(0, remainingSlots);

    if (!acceptedFiles.length) {
      this.feedbackMessage =
        'As imagens selecionadas ja foram adicionadas ou excedem o limite.';
      return;
    }

    const newImages = acceptedFiles.map((file) => this.createImageItem(file));
    this.images = [...this.images, ...newImages];
    this.activeImageId = newImages[newImages.length - 1].id;

    if (nextFiles.length > acceptedFiles.length) {
      this.feedbackMessage =
        'Algumas imagens foram ignoradas por limite, tamanho ou duplicidade.';
    }

    this.emitImagesChange();
  }

  selectImage(imageId: string): void {
    this.activeImageId = imageId;
  }

  goToPrevious(event?: MouseEvent): void {
    event?.stopPropagation();

    if (!this.hasPreviousImage) return;
    this.activeImageId = this.images[this.activeIndex - 1].id;
  }

  goToNext(event?: MouseEvent): void {
    event?.stopPropagation();

    if (!this.hasNextImage) return;
    this.activeImageId = this.images[this.activeIndex + 1].id;
  }

  updateDescription(value: string): void {
    const selectedImage = this.selectedImage;
    if (!selectedImage) return;

    this.images = this.images.map((image) =>
      image.id === selectedImage.id ? { ...image, description: value } : image,
    );
    this.emitImagesChange();
  }

  removeSelectedImage(event?: MouseEvent): void {
    event?.stopPropagation();

    const selectedImage = this.selectedImage;
    if (!selectedImage) return;

    const currentIndex = this.activeIndex;
    URL.revokeObjectURL(selectedImage.url);
    this.images = this.images.filter((image) => image.id !== selectedImage.id);

    const nextIndex = Math.min(currentIndex, this.images.length - 1);
    this.activeImageId = this.images[nextIndex]?.id ?? null;
    this.feedbackMessage = '';
    this.emitImagesChange();
  }

  getNewFiles(): File[] {
    return this.images.map((image) => image.file);
  }

  getNewFilesDecriptions(): string[] {
    return this.images.map((image) => image.description || '');
  }

  getPayloadUpload(): ImageUploaderPayload {
    return {
      files: this.getNewFiles(),
      entity: this.entity.name,
      objectUuid: this.entity.uuid,
      descriptions: this.getNewFilesDecriptions(),
    };
  }

  formatFileSize(size: number): string {
    if (size < 1024 * 1024) {
      return `${Math.max(1, Math.round(size / 1024))} KB`;
    }

    return `${(size / 1024 / 1024).toFixed(1)} MB`;
  }

  ngOnDestroy(): void {
    this.images.forEach((image) => URL.revokeObjectURL(image.url));
  }

  private isFileSizeAllowed(file: File): boolean {
    return file.size <= this.maxFileSizeMb * 1024 * 1024;
  }

  private isDuplicatedFile(file: File): boolean {
    return this.images.some(
      (image) =>
        image.name === file.name &&
        image.size === file.size &&
        image.lastModified === file.lastModified,
    );
  }

  private applyInitialPayload(): void {
    const files = this.initialPayload?.files ?? [];
    const descriptions = this.initialPayload?.descriptions ?? [];
    const signature = files
      .map(
        (file, index) =>
          `${file.name}:${file.size}:${file.lastModified}:${
            descriptions[index] ?? ''
          }`,
      )
      .join('|');

    if (signature === this.initialPayloadSignature) {
      return;
    }

    this.initialPayloadSignature = signature;
    this.images.forEach((image) => URL.revokeObjectURL(image.url));
    this.images = files.map((file, index) =>
      this.createImageItem(file, descriptions[index] ?? ''),
    );
    this.activeImageId = this.images[this.images.length - 1]?.id ?? null;
    this.feedbackMessage = '';
    this.emitImagesChange();
  }

  private createImageItem(file: File, description = ''): ImageUploaderItem {
    return {
      id: this.createImageId(),
      file,
      name: file.name,
      url: URL.createObjectURL(file),
      description,
      size: file.size,
      lastModified: file.lastModified,
    };
  }

  private createImageId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  private emitImagesChange(): void {
    this.imagesChange.emit(this.images);
  }
}
